import Button from "@/components/Button";
import { Checkbox } from "@/components/chadcn/Checkbox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/chadcn/Dialog";
import { Label } from "@/components/chadcn/Label";
import { Textarea } from "@/components/chadcn/TextArea";
import { NumberInput, SelectInput } from "@/components/InputFields";
import Spinner from "@/components/Spinner";
import Toast from "@/components/Toast";
import { useAddClinicalNoteMutation, usePrescribingCaregiversQuery } from "@/data/queries";
import { Caregiver } from "@/model/Caregiver";
import ClinicalProtocolType from "@/model/ClinicalProtocolType";
import { Organization } from "@/model/Organization";
import { Duration } from "@js-joda/core";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";

type FormValues = {
  sendToEmr?: boolean;
  resetDaysSinceReviewed?: boolean;
  assignToCaregiverId?: string;
  timeSpentInMinutes: number;
  livePatientInteraction?: boolean;
  notes: string;
};

type AddClinicalNoteDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  patientId: string;
  protocolType: ClinicalProtocolType;
  prescribingCaregiver: Caregiver;
  currentTableContainsAllNewReadings: boolean;
  organization: Organization;
};

export const AddClinicalNoteDialog = ({
  open,
  onOpenChange,
  patientId,
  protocolType,
  prescribingCaregiver,
  currentTableContainsAllNewReadings,
  organization,
}: AddClinicalNoteDialogProps) => {
  const {
    mutateAsync: addClinicalNote,
    isPending: isAddingClinicalNote,
    isError: isAddClinicalNoteError,
  } = useAddClinicalNoteMutation({ patientId });

  const {
    data: caregivers = [],
    isLoading: isLoadingCaregivers,
    isError: isErrorCaregivers,
  } = usePrescribingCaregiversQuery();

  const isEmrEnabled = organization.emrType !== "none";

  const defaultValues = {
    sendToEmr: isEmrEnabled ? undefined : false,
    resetDaysSinceReviewed: false,
    assignToCaregiverId: isEmrEnabled ? prescribingCaregiver?.id : undefined,
    timeSpentInMinutes: 5,
    livePatientInteraction: false,
    notes: "",
  };

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues,
  });

  const {
    sendToEmr,
    resetDaysSinceReviewed,
    assignToCaregiverId,
    timeSpentInMinutes,
    livePatientInteraction,
    notes,
  } = watch();

  useEffect(() => {
    if (sendToEmr !== undefined) {
      setValue("resetDaysSinceReviewed", sendToEmr ?? false);
    }
  }, [sendToEmr, setValue]);

  useEffect(() => {
    if (prescribingCaregiver) {
      setValue("assignToCaregiverId", prescribingCaregiver.id);
    }
  }, [prescribingCaregiver, setValue]);

  const incrementTimeSpent = () => {
    setValue("timeSpentInMinutes", timeSpentInMinutes + 1);
  };

  const decrementTimeSpent = () => {
    setValue("timeSpentInMinutes", Math.max(0, timeSpentInMinutes - 1));
  };

  const displayReportQueuedToEmrToast = () => {
    toast.custom(
      (toastId) => (
        <Toast
          description="The report is being generated and will be sent to the EMR shortly"
          toastId={toastId}
        />
      ),
      { duration: 7000 }
    );
  };

  const onSubmitInternal = async (form: FormValues) => {
    try {
      await addClinicalNote({
        type: protocolType,
        timeSpent: Duration.ofMinutes(form.timeSpentInMinutes),
        actionType: form.resetDaysSinceReviewed ? "review" : "note",
        livePatientInteraction: form.livePatientInteraction ?? false,
        notes: form.notes,
        assignToCaregiverId:
          isEmrEnabled && form.sendToEmr && form.assignToCaregiverId
            ? form.assignToCaregiverId
            : null,
        sendNotifications: isEmrEnabled && form.sendToEmr ? true : false,
      });
      handleOpenChange(false);
      if (isEmrEnabled && form.sendToEmr) {
        displayReportQueuedToEmrToast();
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleOpenChange = (open: boolean) => {
    onOpenChange(open);
    reset(defaultValues);
  };

  const canSubmit =
    !isAddingClinicalNote &&
    !isNaN(timeSpentInMinutes) &&
    timeSpentInMinutes >= 0 &&
    (isEmrEnabled ? sendToEmr !== undefined : true) &&
    (isEmrEnabled ? livePatientInteraction !== undefined : true) &&
    resetDaysSinceReviewed !== undefined &&
    notes.trim() !== "";

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="flex min-w-[720px] max-w-[10%] flex-col gap-y-4 bg-white">
        <DialogTitle>Add note</DialogTitle>
        <DialogHeader className="flex gap-y-4">
          {!currentTableContainsAllNewReadings && (
            <p className="flex items-center gap-2 text-pretty rounded-md border border-blue-200 bg-blue-100 p-2 text-primary/90">
              <span className="material-icons">warning</span>There are some new measurements that
              are not visible on this page
            </p>
          )}
        </DialogHeader>
        <form
          key={`${organization.id}-${isEmrEnabled}`}
          className="flex flex-col gap-y-4"
          onSubmit={handleSubmit(onSubmitInternal)}
        >
          {isEmrEnabled && (
            <>
              <div className="flex flex-col">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-semibold text-primary/90">
                    Do you want to send the report to the patient's chart in the EMR?{" "}
                    <span className="text-red-500">*</span>
                  </span>
                  <YesNoCheckboxes
                    disabled={isAddingClinicalNote}
                    checked={sendToEmr}
                    onCheckedChange={(checkedState) => setValue("sendToEmr", checkedState === true)}
                  />
                </div>
                <div
                  className={`overflow-hidden transition-all duration-300 ease-in-out ${
                    sendToEmr === true ? "mt-4 max-h-32 opacity-100" : "mt-0 max-h-0 opacity-0"
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-semibold text-primary/90">Assign to provider</span>
                    <SelectInput
                      className="max-w-72"
                      value={assignToCaregiverId || ""}
                      options={[
                        { value: "", title: "None" },
                        ...(isErrorCaregivers
                          ? []
                          : caregivers.map((caregiver) => ({
                              value: caregiver.id,
                              title: caregiver.displayName,
                            }))),
                      ]}
                      onChange={(value) => setValue("assignToCaregiverId", value)}
                      disabled={isAddingClinicalNote || isLoadingCaregivers}
                    />
                    {isLoadingCaregivers && <Spinner className="ms-2" />}
                    {isErrorCaregivers && (
                      <span className="text-sm text-red-500">Failed to load providers</span>
                    )}
                  </div>
                </div>
              </div>
              <hr className="border-divider" />
            </>
          )}
          <div className="flex flex-col">
            <div className="flex items-center justify-between gap-2">
              <span className="font-semibold text-primary/90">
                Do you want to reset the "Days since review"?{" "}
                <span className="text-red-500">*</span>
              </span>
              <YesNoCheckboxes
                disabled={isAddingClinicalNote}
                checked={resetDaysSinceReviewed}
                onCheckedChange={(checkedState) =>
                  setValue("resetDaysSinceReviewed", checkedState === true)
                }
              />
            </div>
          </div>
          <hr className="border-divider" />
          <div className="flex items-center justify-between">
            <Label htmlFor="review-duration-input" className="text-base font-semibold">
              Time spent (minutes) <span className="text-red-500">*</span>
            </Label>
            <div className="flex gap-2">
              <Button
                variant="neutral"
                type="button"
                disabled={isAddingClinicalNote}
                className="material-icons size-10 p-0 text-slate-700"
                onClick={decrementTimeSpent}
              >
                remove
              </Button>
              <NumberInput
                id="review-duration-input"
                disabled={isAddingClinicalNote}
                className="h-10 w-14 border-gray-300 text-center font-medium"
                {...register("timeSpentInMinutes", { valueAsNumber: true })}
              />
              <Button
                variant="neutral"
                type="button"
                disabled={isAddingClinicalNote}
                className="material-icons size-10 p-0 text-slate-700"
                onClick={incrementTimeSpent}
              >
                add
              </Button>
            </div>
          </div>
          <hr className="border-divider" />
          <div className="flex items-center justify-between gap-2">
            <div className="flex flex-col">
              <Label className="text-base font-semibold">
                Did you speak with the patient? <span className="text-red-500">*</span>
              </Label>
              <span className="text-primary/90">(e.g. by phone)</span>
            </div>
            <YesNoCheckboxes
              disabled={isAddingClinicalNote}
              checked={livePatientInteraction}
              onCheckedChange={(checkedState) =>
                setValue("livePatientInteraction", checkedState === true)
              }
            />
          </div>
          <div>
            <div className="mb-2 flex flex-row justify-between">
              <Label htmlFor="review-notes-input" className="mt-2 text-xl font-bold">
                Notes <span className="text-red-500">*</span>
              </Label>
            </div>
            <Textarea
              id="review-notes-input"
              className="h-32 border-gray-300"
              disabled={isAddingClinicalNote}
              {...register("notes")}
            />
          </div>
          {errors.notes && <p className="mt-1 text-red-600">{errors.notes.message}</p>}
          {isAddClinicalNoteError && (
            <p className="mt-1 text-red-600">
              There was an error submitting the note. Please contact Bloomlife Support, or try again
              later.
            </p>
          )}
          <DialogFooter>
            <div className="flex gap-2">
              <Button
                variant="neutral"
                type="button"
                disabled={isAddingClinicalNote}
                onClick={() => handleOpenChange(false)}
                className="w-fit border-none bg-primary/10"
              >
                Cancel
              </Button>
              <Button variant="primary" type="submit" disabled={!canSubmit} className="w-fit">
                Submit
                {isAddingClinicalNote && <Spinner className="ms-2 size-4 border-2" />}
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

type YesNoCheckboxesProps = {
  disabled: boolean;
  checked?: boolean;
  onCheckedChange: (isChecked: boolean) => void;
};

const YesNoCheckboxes = ({ disabled, checked, onCheckedChange }: YesNoCheckboxesProps) => {
  return (
    <div className="flex items-center gap-2">
      <Checkbox
        disabled={disabled}
        checked={checked === undefined ? false : checked}
        onCheckedChange={(checkedState) => onCheckedChange(checkedState === true)}
      />
      <span className="me-4 text-primary/90">Yes</span>
      <Checkbox
        disabled={disabled}
        checked={checked === undefined ? false : !checked}
        onCheckedChange={(checkedState) => onCheckedChange(checkedState === false)}
      />
      <span className="text-primary/90">No</span>
    </div>
  );
};
